import { type RefObject, useEffect, useState } from "react";

export type EntranceState = "idle" | "hidden" | "visible";

export type EntranceVariant = "hidden" | "visible";

interface UseEntranceOptions {
	amount?: number;
	margin?: string;
	disabled?: boolean;
}

export const toEntranceVariant = (state: EntranceState): EntranceVariant => (state === "hidden" ? "hidden" : "visible");

export function useEntrance(
	ref: RefObject<Element | null>,
	{ amount = 0.2, margin = "0px 0px -10% 0px", disabled }: UseEntranceOptions = {},
) {
	const [state, setState] = useState<EntranceState>("idle");

	useEffect(() => {
		const node = ref.current;
		if (!node || disabled) return;
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (!entry) return;
				if (entry.isIntersecting) {
					setState("visible");
					observer.disconnect();
					return;
				}
				setState((prev) => (prev === "idle" ? "hidden" : prev));
			},
			{ threshold: amount, rootMargin: margin },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [ref, amount, margin, disabled]);

	return state;
}
